import type {
	BeckyModel,
	BeckyRequestPayload,
} from '@wix/thunderbolt-becky-root'
import {
	getCompsOfFirstLevelContainersInFold,
	deStringifyExperiments,
	getMergedDataWithFlatPageStructure,
	getCompsInFirstFold,
	HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP,
	HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE,
	geResponsiveCompsInFirstFold,
	getRequestedPageCompId,
	mergeWithExternalSkinsInfo,
	loadTranslations,
	getBuilderComponents,
	fixStructureWithBuilderComponents,
	getBuilderComponentRefSchemas,
	fixStructureWithBuilderLegacyComponents,
} from '@wix/thunderbolt-becky-root'
import type {
	BeckyTopology,
	FetchFn,
	Logger,
	MetricsReporter,
	PathToSelector,
	BuilderTypeToReducedManifest,
	BuilderTypeToPresetInfo,
	RefSchemas,
} from '@wix/thunderbolt-becky-types'
import { mergeMappers } from '@wix/thunderbolt-components'
import type {
	BeckyExperiments,
	BuilderComponentsHierarchy,
	Translations,
} from '@wix/thunderbolt-commons'
import {
	getBuilderComponentsInStructure,
	getAllBuilderComponentsOnPage,
	getCompTypeToAllDescendants,
	resolveRequiredServices,
	getBuilderLegacyComponentsMap,
	deStringifyBuilderApps,
	getBuilderComponentHierarchy,
	calculateBuilderTypeToPresetInfo,
	buildBuilderComponentsOnPageImports,
} from '@wix/thunderbolt-commons'
import {
	getComponentMappers,
	getComponentSkinParams,
} from './componentsSiteAssets'
import { getBuilderTypeToInteractionsConfig } from './interactionsSiteAssets'
import { applyAnywhereThemeOverride } from './themeOverrides'
import { getBuilderTypeToReducedManifest } from '../utils/builderManifestFetch'

export type ResolveBeckyModelParams = {
	payload: BeckyRequestPayload
	fetchFn: FetchFn
	beckyTopology: BeckyTopology
	shapeStore?: Record<string, string>
	builderPresetsStore?: BeckyModel['builderPresetsStore']
	metricsReporter: MetricsReporter
	logger: Logger
	compIdToPathToSelector?: Record<string, PathToSelector>
	builderTypeToPresetInfo?: Record<string, Map<string, { presetVariableKey: string | undefined; presetHashes: Record<string, string | undefined> }>>
	compIdToBuilderType?: Record<string, string>
}

export const resolveBeckyModel = async ({
	payload,
	beckyTopology: { publicTopology, environmentTopology },
	fetchFn,
	shapeStore = {},
	builderPresetsStore = {},
	metricsReporter,
	logger,
	compIdToPathToSelector = {},
	compIdToBuilderType = {},
}: ResolveBeckyModelParams): Promise<BeckyModel> => {
	const {
		pages,
		viewMode = 'desktop',
		beckyExperiments,
		isWixCodeOnPage: isWixCodeOnPageParam,
		isWixCodeOnSite: isWixCodeOnSiteParam,
		isClientSdkOnSite: isClientSdkOnSiteParam,
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam,
		freemiumBanner: freemiumBannerParam,
		coBrandingBanner: coBrandingBannerParam,
		dayfulBanner: dayfulBannerParam,
		mobileActionsMenu: mobileActionsMenuParam,
		shouldSplitByPresets,
		externalBaseUrl,
		metaSiteId,
		siteId,
		clientSpecMap,
		language,
		originalLanguage,
		isWixSite,
		languageResolutionMethod,
		isMultilingualEnabled,
		useSandboxInHTMLComp,
		routersInfo,
		deviceType,
		qaMode,
		testMode,
		debugMode,
		isInSeo: isInSeoParam,
		isPremiumDomain,
		migratingToOoiWidgetIds,
		stylableInstance,
		registryLibrariesTopology,
		isHttps,
		anywhereThemeOverride,
		seoTranslations,
		formFactor,
		disableStaticPagesUrlHierarchy,
		aboveTheFoldSectionsNum,
		isTrackClicksAnalyticsEnabled,
		editorName,
		isSocialElementsBlocked,
		cssPerBreakpointWidgetIds,
		appDefinitionIdsWithCustomCss: appDefinitionIdsWithCustomCssRaw,
		builderAppVersions,
		onlyInteractions,
	} = payload
	const isBuilderComponentModel = payload.isBuilderComponentModel === 'true'
	const isResponsive = payload.isResponsive === 'true'
	const experiments: BeckyExperiments =
		deStringifyExperiments(beckyExperiments)
	const shouldRenderWixAds = freemiumBannerParam === 'true'
	const shouldShowCoBrandingBanner = coBrandingBannerParam === 'true'
	const shouldShowDayfulBanner = dayfulBannerParam === 'true'
	const appDefinitionIdsWithCustomCss = appDefinitionIdsWithCustomCssRaw
		? JSON.parse(appDefinitionIdsWithCustomCssRaw)
		: []

	const [processedPages, compMappers, externalSkinParams] = await Promise.all(
		[
			metricsReporter.runAsyncAndReport(
				() =>
					getMergedDataWithFlatPageStructure({
						pages,
						viewMode,
						isResponsive,
						experiments,
						shouldRenderFreemiumBanner: shouldRenderWixAds,
						shouldShowCoBrandingBanner,
						shouldShowDayfulBanner,
						editorName: experiments['specs.thunderbolt.forceStudio']
							? 'Studio'
							: editorName,
						isBuilderComponentModel,
					}),
				'getMergedDataWithFlatPageStructure',
			),
			metricsReporter.runAsyncAndReport(
				() =>
					getComponentMappers({
						fetchFn,
						registryLibrariesTopology,
						metricsReporter,
						isExperimentOpen: (specName: string) =>
							experiments.hasOwnProperty(specName),
						isPlatformBuilderModule:
							payload.module?.startsWith('builder-component'),
						isEditor: payload.isEditor === 'true',
					}),
				'getComponentMappers',
			),
			metricsReporter.runAsyncAndReport(
				() =>
					getComponentSkinParams({
						fetchFn,
						registryLibrariesTopology,
						metricsReporter,
						isExperimentOpen: (specName: string) =>
							experiments.hasOwnProperty(specName),
						isPlatformBuilderModule:
							payload.module?.startsWith('builder-component'),
						isEditor: payload.isEditor === 'true',
					}),
				'getComponentSkinParams',
			),
		],
	)

	const componentsSkinParams = mergeWithExternalSkinsInfo(externalSkinParams)

	const data = anywhereThemeOverride
		? applyAnywhereThemeOverride({
			data: processedPages.data,
			anywhereThemeOverride,
		})
		: processedPages.data

	const isInSeo = isInSeoParam === 'true'

	const siteStructure = {
		...processedPages.structure,
		...processedPages.masterPageStructure,
	}
	const isOneDocMigrated = !!siteStructure.masterPage.layoutQuery

	const compsInFoldThreshold =
		!viewMode || viewMode === 'desktop'
			? HEURISTIC_FIRST_FOLD_HEIGHT_DESKTOP
			: HEURISTIC_FIRST_FOLD_HEIGHT_MOBILE
	const pageCompId = getRequestedPageCompId(pages)
	const compsInFoldMap = isResponsive
		? metricsReporter.runAndReport(
			() =>
				geResponsiveCompsInFirstFold(
					processedPages,
					pageCompId,
					processedPages.data.layout_data,
					processedPages.data.variants_data,
					aboveTheFoldSectionsNum,
				),
			'geResponsiveCompsInFirstFold',
		) || {}
		: isOneDocMigrated
			? metricsReporter.runAndReport(
				() =>
					getCompsOfFirstLevelContainersInFold(
						siteStructure,
						processedPages.data.layout_data,
						viewMode === 'mobile',
						compsInFoldThreshold,
					),
				'getCompsOfFirstLevelContainersInFold',
			)
			: metricsReporter.runAndReport(
				() => getCompsInFirstFold(siteStructure, compsInFoldThreshold),
				'getCompsInFirstFold',
			)

	const builderComponents = getBuilderComponents(clientSpecMap)

	if (builderComponents.size > 0) {
		processedPages.masterPageStructure = fixStructureWithBuilderComponents(
			processedPages.masterPageStructure,
			builderComponents,
		)
		processedPages.structure = fixStructureWithBuilderComponents(
			processedPages.structure,
			builderComponents,
		)
	}

	const builderLegacyComponents = getBuilderLegacyComponentsMap(
		isBuilderComponentModel,
		clientSpecMap,
	)

	if (Object.keys(builderLegacyComponents).length > 0) {
		processedPages.masterPageStructure =
			fixStructureWithBuilderLegacyComponents(
				processedPages.masterPageStructure,
				builderLegacyComponents,
			)
		processedPages.structure = fixStructureWithBuilderLegacyComponents(
			processedPages.structure,
			builderLegacyComponents,
		)
	}

	const globalTranslations: Translations = await loadTranslations(language!)

	const isExperimentOpen = (name: keyof BeckyModel['experiments']) =>
		experiments.hasOwnProperty(name)

	const componentsMappers = metricsReporter.runAndReport(
		() => mergeMappers(compMappers, isExperimentOpen, logger),
		'mergeMappers',
	)

	let compTypeToAllDescendants: BuilderComponentsHierarchy = {}
	let allBuilderComponentsOnPage: Set<string> = new Set<string>()
	let requiredServicesMap = {}
	let appDefIdToRefs = {}
	const dynamicSchemas: RefSchemas = {}
	let builderTypeToInteractionsConfig
	let builderTypeToReducedManifest: BuilderTypeToReducedManifest = {}
	const structureBuilderTypeToInnerRefPresets: Record<string, BuilderTypeToPresetInfo> = {}
	let builderComponentsOnPageImports: BeckyModel['builderComponentsOnPageImports'] = {
		client: [],
		server: [],
		sdk: []
	}

	if (isBuilderComponentModel) {
		const builderComponentsInStructure = getBuilderComponentsInStructure(processedPages.structure)
		if (builderComponentsInStructure.size > 0) {
			const parsedBuilderAppVersions = deStringifyBuilderApps(logger, builderAppVersions)
			const builderComponentHierarchy = await getBuilderComponentHierarchy(
				parsedBuilderAppVersions,
				fetchFn,
				(e) => logger.error('Error getting builder component hierarchy', e),
				{ publicTopology, environmentTopology },
			)

			compTypeToAllDescendants = getCompTypeToAllDescendants(builderComponentHierarchy)

			allBuilderComponentsOnPage = getAllBuilderComponentsOnPage(
				builderComponentsInStructure,
				compTypeToAllDescendants,
			)

			builderComponentsOnPageImports = buildBuilderComponentsOnPageImports(clientSpecMap, allBuilderComponentsOnPage)

			requiredServicesMap = resolveRequiredServices(
				clientSpecMap,
				allBuilderComponentsOnPage,
				logger,
			)

			const [appDefIdToRefsResult, builderTypeToReducedManifestResult] = await Promise.all([
				getBuilderComponentRefSchemas(clientSpecMap, allBuilderComponentsOnPage, { fetchFn, logger, beckyTopology: { publicTopology, environmentTopology } }),
				getBuilderTypeToReducedManifest(processedPages.structure, clientSpecMap, fetchFn, logger, isBuilderComponentModel, compTypeToAllDescendants, experiments),
			])

			const reuseDynamicRefSchemas = isExperimentOpen('specs.thunderbolt.reuseDynamicRefSchemas')
			if (reuseDynamicRefSchemas) {
				// keep under new name & format when experiment is on
				Object.values(appDefIdToRefsResult).forEach((refSchemas) => {
					for (const mapName in refSchemas) {
						if (!dynamicSchemas[mapName]) {
							dynamicSchemas[mapName] = {}
						}
						Object.assign(dynamicSchemas[mapName], refSchemas[mapName])
					}
				})
			} else {
				// keep under previous name & format when experiment is off
				appDefIdToRefs = appDefIdToRefsResult
			}

			builderTypeToReducedManifest = builderTypeToReducedManifestResult

			if (experiments['specs.thunderbolt.interactionsFeature']) {
				builderTypeToInteractionsConfig = await metricsReporter.runAsyncAndReport(
					() =>
						getBuilderTypeToInteractionsConfig(
							fetchFn,
							logger,
							clientSpecMap,
							allBuilderComponentsOnPage,
						),
					'getBuilderTypeToInteractionsConfig',
				)
			}

			if (experiments['specs.thunderbolt.supportExternalDependenciesCss']) {
				builderComponentsInStructure.forEach((rootBuilderType) => {
					if (structureBuilderTypeToInnerRefPresets[rootBuilderType]) {
						return
					}
					const descendantBuilderTypes = compTypeToAllDescendants[rootBuilderType] || []

					// build ref for presetCompNode in Public flow
					const builderTypeToPresetInfo = calculateBuilderTypeToPresetInfo({
						rootBuilderType,
						descendantBuilderTypes,
						builderTypeToReducedManifest,
					})

					structureBuilderTypeToInnerRefPresets[rootBuilderType] = builderTypeToPresetInfo
				})
			}
		}
	}

	return {
		...processedPages,
		appDefIdToRefs,
		dynamicSchemas,
		data,
		seoTranslations,
		externalBaseUrl,
		metaSiteId,
		siteId,
		previousStore: new WeakMap(),
		viewMode: viewMode || 'desktop',
		environmentTopology,
		publicTopology,
		language: language!,
		isMultilingualEnabled: isMultilingualEnabled === 'true',
		disableStaticPagesUrlHierarchy:
			disableStaticPagesUrlHierarchy === 'true',
		languageResolutionMethod,
		originalLanguage,
		useSandboxInHTMLComp: useSandboxInHTMLComp === 'true',
		isResponsive,
		editorName,
		isWixSite: isWixSite === 'true',
		hasTPAWorkerOnSite: hasTPAWorkerOnSiteParam === 'true',
		fetchRequestsStore: {},
		afterFirstRecalculate: false,
		isWixCodeOnPage: isWixCodeOnPageParam === 'true',
		isWixCodeOnSite: isWixCodeOnSiteParam === 'true',
		isClientSdkOnSite: isClientSdkOnSiteParam === 'true',
		shouldSplitByPresets: shouldSplitByPresets === 'true',
		renderFlags: {
			isWixAdsAllowed: shouldRenderWixAds,
			isWixBlocks: false,
			showScreenInComp: isInSeo,
			componentViewMode: 'preview',
			componentPreviewStates: {},
			isBackToTopButtonAllowed: false,
			shouldBlockGoogleMapInteraction: false,
			shouldResetTinyMenuZIndex: false,
			shouldResetSlideShowNextPrevButtonsPosition: false,
			shouldResetSubscribeFormMinMaxWidth: false,
			shouldUpdateJsonFromMeasureMap: false,
			isPlayingAllowed: false,
			isSocialInteractionAllowed: false,
			isExternalNavigationAllowed: true,
			isSlideShowGalleryClickAllowed: true,
			renderFixedPositionContainers: true,
			shouldResetGalleryToOriginalState: false,
			shouldResetComponent: true,
			siteScale: 1,
			isComponentTransitionAllowed: true,
			allowShowingFixedComponents: true,
			isZoomAllowed: false,
			renderFixedPositionBackgrounds: false,
			extraSiteHeight: 0,
			ignoreComponentsHiddenProperty: [],
			ignoreComponentsCollapsedProperty: [],
			transparentComponentsOverlay: -1,
			showHiddenComponents: false,
			blockingLayer: null,
			allowSiteOverflow: true,
			renderScrollSnap: true,
			renderSticky: true,
			compsToRenderSticky: {},
			enablePageAutoGrowForDetachedComponents: true,
			shouldRenderTPAsIframe: true,
			isAdi: false,
			isLivePreviewOpenForEditor: true,
			isExternalComponentsLoading: false,
			forceDisableSiteOverflow: false,
			vhScalingFactor: null,
		},
		meshRuntimeData: {
			absoluteComps: [],
		},
		shouldRenderWixAdsOnNavigation: shouldRenderWixAds,
		shouldShowCoBrandingBanner,
		shouldShowDayfulBanner,
		shouldShowMobileActionsMenu: mobileActionsMenuParam === 'true',
		isPremiumDomain: isPremiumDomain === 'true',
		clientSpecMap,
		componentsMappers,
		componentsSkinParams,
		experiments,
		routersInfo: routersInfo!,
		deviceType: deviceType!,
		qaMode: qaMode === 'true',
		testMode: testMode === 'true',
		debugMode: debugMode === 'true',
		isInSeo,
		migratingToOoiWidgetIds: migratingToOoiWidgetIds
			? migratingToOoiWidgetIds.split(',')
			: [],
		stylableInstance,
		themeCounter: 0,
		enableVariantsTransitionsInEditor: true,
		compIdToIgnoredTransformProps: {},
		ignoredTransformPropsForType: {},
		isSocialElementsBlocked: isSocialElementsBlocked === 'true',
		globalIgnoredTransformProps: [],
		isHttps: isHttps === 'true',
		shapeStore,
		builderPresetsStore,
		compsInFoldMap,
		imageCompsTargetScale: 1,
		formFactor,
		anywhereThemeOverride,
		activeVariant: '',
		activeBreakpoints: {},
		globalTranslations,
		ooiCssConfigStore: {},
		ooiWidgetsAppSettings: {},
		isTrackClicksAnalyticsEnabled: isTrackClicksAnalyticsEnabled === 'true',
		isOneDocMigrated,
		cssPerBreakpointWidgetIds: cssPerBreakpointWidgetIds
			? cssPerBreakpointWidgetIds.split(',')
			: [],
		requestedPageId: payload.pageId || payload.pageCompId || undefined,
		widgetIdToPageIdMap: payload.widgetIdToPageIdMap,
		appDefinitionIdsWithCustomCss,
		compIdToPathToSelector,
		...(builderTypeToInteractionsConfig && { builderTypeToInteractionsConfig }),
		builderTypeToReducedManifest,
		builderTypeToPresetInfo: structureBuilderTypeToInnerRefPresets,
		isBuilderComponentModel,
		compTypeToAllDescendants,
		compTypeToAllDescendantsLoading: { state: 'DONE' },
		requiredServicesMap,
		compIdToBuilderType,
		onlyInteractions: onlyInteractions ?? false,
		allBuilderComponentsOnPage: Array.from(allBuilderComponentsOnPage),
		builderComponentsOnPageImports,
	}
}
